import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import { t } from 'src/lib/language';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import {
  DELIVERY_TYPE,
  DownloadOptionsModel,
  EXTENSION,
  FORMAT,
  GROUP_BY_ITEMS,
  ORIENTATION,
} from 'src/models/DownloadModel';
import { FORM } from 'src/constants/Form';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import { makeStyles } from '@material-ui/core/styles';
import { DIALOGS } from 'src/models/DialogModel';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { setError } from 'src/redux/actions/errorsActions';
import { useState } from 'react';
import { closeDialog } from 'src/redux/actions/dialogActions';
import {
  postDownloadEntitesAction,
  postDownloadSelectedEntitesAction,
  postEmailEntitesAction,
  postEmailSelectedEntitesAction,
} from 'src/redux/actions/querybuilderActions';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import FormSingleSelect from 'src/components/Control/FormControls/FormSingleSelect';

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
  },
}));

enum InvoiceType {
  all = 'all',
  selected = 'selected',
  result = 'result',
}

enum InvoiceFormat {
  invoice = 'invoice',
  statement = 'statement',
  pdf = 'pdf',
}

const DownloadInvoiceDialog = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const opened = useSelector(
    (state: Store) => state.dialog[DIALOGS.invoiceDownload]?.opened || false,
  );
  const count = useSelector(
    (state: Store) => (state.querybuilder[ENTITIES.invoices]
        && (state.querybuilder[ENTITIES.invoices].result?.count || 0)),
  );
  const selections = useSelector(
    (state: Store) => state.selected[ENTITIES.invoices] || [],
  );
  const [options, setOptions] = useState({
    file: 'invoices',
    orientation: ORIENTATION.portrait,
    format: FORMAT.pdf,
    delivery: DELIVERY_TYPE.download,
    export_to_grid: false,
    group_by: null,
  } as DownloadOptionsModel);

  const [type, setType] = useState(InvoiceType.selected as InvoiceType);
  const extension = options.format === FORMAT.csv ? EXTENSION.csv : EXTENSION.pdf;

  const handleCancel = () => {
    dispatch(closeDialog({ dialog: DIALOGS.invoiceDownload }));
  };

  const handleDownload = () => {
    dispatch(setError({ [FORM.download]: {} }));

    if (!options.file) {
      options.file = 'invoices';
    }
    options.file = `${options.file.split('.')[0]}.${extension}`;
    if (type === InvoiceType.all) {
      options.all = true;
    }

    if (!options.export_to_grid) {
      options.format = options.format === FORMAT.statement ? FORMAT.statement : FORMAT.pdf;
      delete options.group_by;
    }

    if (type === InvoiceType.all) {
      options.all = true;
      if (options.delivery === DELIVERY_TYPE.download) {
        dispatch(postDownloadEntitesAction(ENTITIES.invoices, options));
      } else {
        dispatch(postEmailEntitesAction(ENTITIES.invoices, options));
      }
    }

    if (type === InvoiceType.result) {
      options.all = false;
      if (options.delivery === DELIVERY_TYPE.download) {
        dispatch(postDownloadEntitesAction(ENTITIES.invoices, options));
      } else {
        dispatch(postEmailEntitesAction(ENTITIES.invoices, options));
      }
    }

    if (type === InvoiceType.selected) {
      options.all = false;
      if (options.delivery === DELIVERY_TYPE.download) {
        dispatch(postDownloadSelectedEntitesAction(ENTITIES.invoices, selections, options));
      } else {
        dispatch(postEmailSelectedEntitesAction(ENTITIES.invoices, selections, options));
      }
    }

    dispatch(closeDialog({ dialog: DIALOGS.invoiceDownload }));
  };

  const onFieldChange = (value: Value, field: string) => {
    setOptions({
      ...options,
      [field]: value,
    });
  };

  const canDownload = () => {
    if (type === InvoiceType.selected) {
      return selections.length < 1000;
    }
    if (type === InvoiceType.result) {
      return count < 1000;
    }
    return false;
  };

  return (
    <Dialog
      aria-labelledby="download-invoice"
      open={opened}>
      <DialogTitle id="download-invoice">Get Invoices</DialogTitle>
      <DialogContent dividers>
        <div style={{ width: '480px' }}>
          <FormControl component="fieldset" style={{ marginTop: '8px' }}>
            <FormLabel component="legend">Invoice selection</FormLabel>
            <RadioGroup
              row
              aria-label="invoices"
              name="invoices"
              value={type}
              onChange={(e) => setType(e.target.value as InvoiceType)}>
              <FormControlLabel value={InvoiceType.all} control={<Radio />} label="All" />
              <FormControlLabel value={InvoiceType.selected} control={<Radio />} label={`Selected  (${selections.length})`} />
              <FormControlLabel value={InvoiceType.result} control={<Radio />} label={`Result (${count})`} />
            </RadioGroup>
          </FormControl>
          <div className={classes.row}>
            <FormTextField
              form={FORM.download}
              label="File Name"
              name="file"
              onChange={onFieldChange}
              required
              value={options.file.split('.')[0]} />
            { `.${extension}` }
          </div>
          <div style={{ marginTop: '8px' }} />
          <FormCheckbox
            checked={options.export_to_grid}
            label="Export"
            name="export_to_grid"
            onChange={(checked:boolean) => onFieldChange(checked, 'export_to_grid')} />
          <div />
          { options.export_to_grid && (
            <FormSingleSelect
              form={FORM.download}
              items={GROUP_BY_ITEMS}
              label="Group By"
              name="group_by"
              onChange={onFieldChange}
              value={options.group_by ?? 'null'} />
          )}
          <div />
          <FormControl
            component="fieldset"
            style={{ marginTop: '8px' }}>
            <FormLabel component="legend">Orientation</FormLabel>
            <RadioGroup
              row
              aria-label="orientation"
              name="orientation"
              value={options.orientation || ORIENTATION.portrait}
              onChange={(e) => onFieldChange(e.target.value, 'orientation')}>
              <FormControlLabel
                value={ORIENTATION.portrait}
                control={<Radio />}
                label={ORIENTATION.portrait} />
              <FormControlLabel
                value={ORIENTATION.landscape}
                control={<Radio />}
                label={ORIENTATION.landscape} />
            </RadioGroup>
          </FormControl>
          <div />
          { !options.export_to_grid && (
            <>
              <FormControl component="fieldset" style={{ marginTop: '8px' }}>
                <FormLabel component="legend">Invoices or Statements</FormLabel>
                <RadioGroup
                  row
                  aria-label="format"
                  name="format"
                  value={options.format || InvoiceFormat.pdf}
                  onChange={(e) => onFieldChange(e.target.value, 'format')}>
                  <FormControlLabel
                    value={InvoiceFormat.pdf}
                    control={<Radio />}
                    label={InvoiceFormat.invoice} />
                  <FormControlLabel
                    value={InvoiceFormat.statement}
                    control={<Radio />}
                    label={InvoiceFormat.statement} />
                </RadioGroup>
              </FormControl>
              <FormControl
                component="fieldset"
                style={{ marginTop: '8px' }}>
                <FormLabel component="legend">Delivery Type</FormLabel>
                <RadioGroup
                  row
                  aria-label="Delivery Type"
                  name="download"
                  value={options.delivery || DELIVERY_TYPE.email}
                  onChange={(e) => onFieldChange(e.target.value, 'delivery')}>
                  <FormControlLabel
                    value={DELIVERY_TYPE.email}
                    control={<Radio />}
                    label="Email Admin" />
                  <FormControlLabel
                    value={DELIVERY_TYPE.contact}
                    control={<Radio />}
                    label="Email Contact" />
                  { canDownload() && (
                  <FormControlLabel
                    value={DELIVERY_TYPE.download}
                    control={<Radio />}
                    label="Download" />
                  )}
                </RadioGroup>
              </FormControl>
            </>
          )}
          { options.export_to_grid && (
            <>
              <FormControl component="fieldset" style={{ marginTop: '8px' }}>
                <FormLabel component="legend">Format</FormLabel>
                <RadioGroup
                  row
                  aria-label="format"
                  name="format"
                  value={options.format || FORMAT.pdf}
                  onChange={(e) => onFieldChange(e.target.value, 'format')}>
                  <FormControlLabel value={FORMAT.pdf} control={<Radio />} label={FORMAT.pdf} />
                  <FormControlLabel value={FORMAT.csv} control={<Radio />} label={FORMAT.csv} />
                </RadioGroup>
              </FormControl>
              <div />
              <FormControl
                component="fieldset"
                style={{ marginTop: '8px' }}>
                <FormLabel component="legend">Delivery Type</FormLabel>
                <RadioGroup
                  row
                  aria-label="Delivery Type"
                  name="download"
                  value={options.delivery || DELIVERY_TYPE.email}
                  onChange={(e) => onFieldChange(e.target.value, 'delivery')}>
                  <FormControlLabel
                    value={DELIVERY_TYPE.email}
                    control={<Radio />}
                    label="By Email" />
                  { canDownload() && (
                    <FormControlLabel
                      value={DELIVERY_TYPE.download}
                      control={<Radio />}
                      label="Download" />
                  )}
                </RadioGroup>
              </FormControl>
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel} color="primary">
          { t(lang, 'misc.cancel') }
        </Button>
        <Button onClick={handleDownload} color="primary">
          { t(lang, 'misc.download') }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DownloadInvoiceDialog;
